import type {
  ReportMetricOutputSummaryResource,
  ReportRowResource,
} from "@/types_api/backend/transport/report_rows_response"
import type {
  MatchType as ConversationMetadataMatchType,
  Operator as ConversationsMetricValueOperator,
  ValueType as ConversationsMetricValueType,
  UploadedConversationReportRowsRequest,
} from "@/types_api/backend/transport/uploaded_conversation_report_rows_request"
import type { JsonObject } from "@/types/json"
import {
  normalizeMetricConfig,
  type MetricViews,
} from "@/types/MetricViewConfig"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowDetailLike, RunRowLike } from "@/types/RunRow"

const DEFAULT_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT = 100
const MIN_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT = 1
const MAX_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT = 2000

type LegacyMonitoringReportFilters = Record<string, unknown> | null | undefined

export type UploadedConversationReportRowsTransportFilters = NonNullable<
  UploadedConversationReportRowsRequest["filters"]
> & {
  tag_filters?: string[]
}

export function clampUploadedConversationReportRowsLimit(
  limit: number | undefined,
): number {
  if (typeof limit !== "number" || !Number.isFinite(limit)) {
    return DEFAULT_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT
  }

  return Math.min(
    Math.max(Math.trunc(limit), MIN_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT),
    MAX_UPLOADED_CONVERSATION_REPORT_ROWS_LIMIT,
  )
}

function getString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function getStringLike(value: unknown): string {
  if (typeof value === "string") {
    return value
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return ""
}

function getStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : undefined
}

function getRecordArray(
  value: unknown,
): Array<Record<string, unknown>> | undefined {
  return Array.isArray(value) &&
    value.every(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null && !Array.isArray(item),
    )
    ? value
    : undefined
}

function getUploadedConversationMetricValueOperator(
  value: unknown,
): ConversationsMetricValueOperator {
  return value === ">" ||
    value === "<" ||
    value === "=" ||
    value === ">=" ||
    value === "<=" ||
    value === "!=" ||
    value === "range" ||
    value === "exists"
    ? value
    : "="
}

function getUploadedConversationMetricValueType(
  value: unknown,
): ConversationsMetricValueType {
  return value === "float" ? "float" : "string"
}

function getConversationMetadataMatchType(
  value: unknown,
): ConversationMetadataMatchType {
  return value === "CONTAINS" ? "CONTAINS" : "EQUALS"
}

export function buildUploadedConversationReportRowsTransportFilters(
  filters: LegacyMonitoringReportFilters,
): UploadedConversationReportRowsTransportFilters {
  if (!filters) {
    return {}
  }

  const metadataFilters = getRecordArray(
    filters.metadataFilters ?? filters.metadata_filters,
  )?.map((filter) => ({
    key: getString(filter.key) ?? "",
    value: getString(filter.value) ?? "",
    match_type: getConversationMetadataMatchType(
      filter.matchType ?? filter.match_type,
    ),
  }))

  const metricValueFilters = getRecordArray(
    filters.metricValueFilters ?? filters.metric_value_filters,
  )?.map((filter) => ({
    metric_id: getString(filter.metricId ?? filter.metric_id) ?? "",
    operator: getUploadedConversationMetricValueOperator(filter.operator),
    value: getStringLike(filter.value),
    value_type: getUploadedConversationMetricValueType(
      filter.valueType ?? filter.value_type,
    ),
    range_max:
      filter.rangeMax == null && filter.range_max == null
        ? null
        : String(filter.rangeMax ?? filter.range_max),
  }))

  return {
    search_query:
      getString(filters.searchQuery ?? filters.search_query) ?? null,
    search_type:
      filters.searchType === "ALL_FIELDS" ||
      filters.search_type === "ALL_FIELDS"
        ? "ALL_FIELDS"
        : undefined,
    start_date: getString(filters.startDate ?? filters.start_date) ?? null,
    end_date: getString(filters.endDate ?? filters.end_date) ?? null,
    date_field:
      filters.dateField === "occurred_at" ||
      filters.date_field === "occurred_at"
        ? "occurred_at"
        : "created_at",
    metric_ids: getStringArray(filters.metricIds ?? filters.metric_ids),
    agent_ids: getStringArray(filters.agentIds ?? filters.agent_ids),
    tag_filters: getStringArray(filters.tagFilters ?? filters.tag_filters),
    metadata_filters: metadataFilters,
    metric_value_filters: metricValueFilters,
    monitoring_filter:
      typeof filters.monitoringFilter === "boolean"
        ? filters.monitoringFilter
        : typeof filters.monitoring_filter === "boolean"
          ? filters.monitoring_filter
          : null,
  }
}

export function getUploadedConversationSimulationOutputIds(
  filters: LegacyMonitoringReportFilters,
): string[] {
  if (!filters) {
    return []
  }
  return (
    getStringArray(
      filters.simulationOutputIds ?? filters.simulation_output_ids,
    ) ?? []
  )
}

export function resolveReportRowsSimulationOutputIds({
  humanReviewSimulationOutputIds,
  isMonitoring,
  uploadedConversationSimulationOutputIds,
}: {
  humanReviewSimulationOutputIds: string[] | null
  isMonitoring: boolean
  uploadedConversationSimulationOutputIds: string[]
}): string[] | null {
  if (!isMonitoring) {
    return humanReviewSimulationOutputIds
  }

  return uploadedConversationSimulationOutputIds.length > 0
    ? uploadedConversationSimulationOutputIds
    : null
}

interface TransportLoadingStateInput {
  data: unknown
  enabled: boolean
  error: unknown
  isLoading: boolean
}

export function normalizeReportResource<T extends { metric_config?: unknown }>(
  report: T,
): Omit<T, "metric_config"> & { metric_config?: MetricViews | null } {
  return {
    ...report,
    metric_config: normalizeMetricConfig(report.metric_config),
  }
}

function toJsonObject(value: unknown): JsonObject {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as JsonObject
  }

  return {}
}

function toMetricResult(value: unknown): RunMetricOutput["result"] {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as RunMetricOutput["result"]
  }

  return null
}

function toSubvaluesByTimestamp(
  value: ReportMetricOutputSummaryResource["subvalues_by_timestamp"],
): RunMetricOutput["subvalues_by_timestamp"] {
  return Array.isArray(value)
    ? (value as RunMetricOutput["subvalues_by_timestamp"])
    : []
}

function normalizeReportMetricOutput(
  metricOutput: ReportMetricOutputSummaryResource,
): RunMetricOutput {
  const result = toMetricResult(metricOutput.result)

  return {
    simulation_output_id: metricOutput.simulation_output_id ?? "",
    metric_id: metricOutput.metric_id ?? undefined,
    output_type: metricOutput.output_type ?? "",
    float_value: metricOutput.float_value ?? null,
    string_value: metricOutput.string_value ?? null,
    set_value: (metricOutput.set_value ?? null) as RunMetricOutput["set_value"],
    result,
    result_json: toJsonObject(metricOutput.result),
    subvalues_by_timestamp: toSubvaluesByTimestamp(
      metricOutput.subvalues_by_timestamp,
    ),
    error_status: (metricOutput.error_status ??
      null) as RunMetricOutput["error_status"],
    error_message: metricOutput.error_message ?? null,
  }
}

function normalizeReportMetricOutputs(
  metricOutputs: ReportRowResource["metric_outputs"],
): Record<string, RunMetricOutput> {
  if (!metricOutputs) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(metricOutputs).map(([metricId, metricOutput]) => [
      metricId,
      normalizeReportMetricOutput(metricOutput),
    ]),
  )
}

function normalizeReportRow(row: ReportRowResource): RunRowDetailLike {
  return {
    ...row,
    config: toJsonObject(row.config),
    created_at: row.created_at ?? "",
    customer_metadata: toJsonObject(row.customer_metadata),
    error_message: row.error_message ?? null,
    error_status: row.error_status ?? null,
    expected_behaviors: row.expected_behaviors ?? [],
    expected_output_json: toJsonObject(row.expected_output_json),
    explanations: {},
    external_simulation_attributes: {},
    ground_truths: {},
    ground_truths_explanation: {},
    integrations: toJsonObject(row.integrations),
    integration_loaded: false,
    initialization_output: {},
    input_str: row.input_str ?? null,
    is_public: row.is_public ?? false,
    metric_outputs: normalizeReportMetricOutputs(row.metric_outputs),
    metrics: {},
    model_type: row.model_type ?? null,
    notes: row.notes ?? null,
    persona_id: row.persona_id ?? null,
    simulation_metadata_input: {},
    simulation_output_loaded: false,
    simulation_output_str: null,
    simulation_s3_bucket: row.simulation_s3_bucket ?? null,
    simulation_s3_key: row.simulation_s3_key ?? null,
    test_case_description: row.test_case_description ?? null,
    test_case_params: toJsonObject(row.test_case_params) as Record<
      string,
      string
    >,
    test_set_id: row.test_set_id ?? null,
    test_set_row_id: row.test_set_row_id ?? null,
    tool_calls: [],
    transcript: [],
    transcript_type: "raw",
  }
}

export function normalizeReportRows(
  rows: ReportRowResource[] | null | undefined,
): RunRowLike[] | undefined {
  return rows?.map(normalizeReportRow)
}

export function mergeReportRowMetricDetails(
  baseRows: RunRowLike[] | null | undefined,
  detailRows: RunRowLike[] | null | undefined,
  metricId: string | null | undefined,
): RunRowLike[] | undefined {
  if (!baseRows) {
    return undefined
  }
  if (!metricId || !detailRows?.length) {
    return baseRows
  }

  const detailsBySimulationOutputId = new Map(
    detailRows.map((row) => [row.simulation_output_id, row]),
  )

  return baseRows.map((row) => {
    const detailOutput = detailsBySimulationOutputId.get(
      row.simulation_output_id,
    )?.metric_outputs?.[metricId]
    if (!detailOutput) {
      return row
    }

    return {
      ...row,
      metric_outputs: {
        ...row.metric_outputs,
        [metricId]: {
          ...row.metric_outputs[metricId],
          ...detailOutput,
        },
      },
    }
  })
}

export function isReportTransportQueryLoading({
  data,
  enabled,
  error,
  isLoading,
}: TransportLoadingStateInput): boolean {
  return isLoading || (enabled && data == null && error == null)
}
